import { forwardRef } from "react";
import { iconSize } from "@/lib/iconSize";

/**
 * Button — system-wide labeled button used throughout the app.
 *
 * Wraps the existing prc-btn-* CSS variants, keeping the visual identity but
 * standardising icon size, gap and ordering (icon left, label right). Use
 * <IconButton> for icon-only buttons (consistent square shape).
 *
 * Usage:
 *   <Button variant="primary" leftIcon={Plus} onClick={...}>運行を記録</Button>
 *   <Button variant="ghost" rightIcon={ArrowRight}>続きを読む</Button>
 *
 * Variants:
 *   primary | ghost | accent | danger
 *
 * Sizes (control padding/font, NOT icon):
 *   sm | md (default) | lg
 *
 * The optional `iconSize` prop maps to the system tier — "sm" (16) by default.
 * Use "lg" (24) for hero buttons that need bigger glyphs.
 */
const VARIANT_CLS = {
    primary: "prc-btn-primary",
    ghost: "prc-btn-ghost",
    accent: "prc-btn-accent",
    // Rose-toned destructive button — reuses spacing/shape of prc-btn-primary
    danger: "py-2 px-4 rounded-sm font-bold text-sm bg-rose-600 hover:bg-rose-500 text-white transition disabled:opacity-50",
};

const SIZE_CLS = {
    sm: "!py-1.5 !px-3 !text-xs",
    md: "",
    lg: "!py-3 !px-6 !text-base",
};

export const Button = forwardRef(function Button(
    {
        variant = "primary",
        size = "md",
        iconSize: iconSizeKey = "sm",
        leftIcon: LeftIcon,
        rightIcon: RightIcon,
        leftIconWeight = "regular",
        rightIconWeight = "regular",
        className = "",
        children,
        ...props
    },
    ref,
) {
    const cls = [
        VARIANT_CLS[variant] || VARIANT_CLS.primary,
        SIZE_CLS[size] || "",
        "inline-flex items-center gap-2",
        className,
    ].filter(Boolean).join(" ");
    const sz = iconSize(iconSizeKey);
    return (
        <button ref={ref} className={cls} {...props}>
            {LeftIcon ? <LeftIcon size={sz} weight={leftIconWeight} /> : null}
            {children != null && <span>{children}</span>}
            {RightIcon ? <RightIcon size={sz} weight={rightIconWeight} /> : null}
        </button>
    );
});

/**
 * IconButton — square icon-only button. Keeps the existing prc-btn-* visual
 * but enforces consistent shape across the app (no more `!px-2 !py-1` /
 * `!px-3` / `!px-3 !py-2` mismatches). Defaults to a comfortable 36×36 box
 * so the tap target meets ~44px when including padding.
 *
 * Usage:
 *   <IconButton icon={Trash} variant="ghost" tone="danger" onClick={...} />
 *   <IconButton icon={MapPin} variant="ghost" size="sm" title="地図" />
 */
const ICON_BTN_SIZE_CLS = {
    sm: "h-8 w-8",
    md: "h-9 w-9",
    lg: "h-11 w-11",
};

const TONE_CLS = {
    default: "",
    danger: "text-rose-300 hover:text-rose-200",
    success: "text-[var(--prc-green)]",
    warning: "text-amber-300",
};

export const IconButton = forwardRef(function IconButton(
    {
        icon: Icon,
        variant = "ghost",
        size = "md",
        tone = "default",
        iconSize: iconSizeKey = "sm",
        iconWeight = "regular",
        className = "",
        ariaLabel,
        title,
        ...props
    },
    ref,
) {
    const cls = [
        VARIANT_CLS[variant] || VARIANT_CLS.ghost,
        ICON_BTN_SIZE_CLS[size] || ICON_BTN_SIZE_CLS.md,
        "inline-flex items-center justify-center !p-0",
        TONE_CLS[tone] || "",
        className,
    ].filter(Boolean).join(" ");
    const sz = iconSize(iconSizeKey);
    return (
        <button
            ref={ref}
            type={props.type || "button"}
            aria-label={ariaLabel || title}
            title={title}
            className={cls}
            {...props}
        >
            {Icon ? <Icon size={sz} weight={iconWeight} /> : null}
        </button>
    );
});
